import React, {useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Link} from "react-router-dom";
import {Dropdown} from "primereact/dropdown";
import {Calendar} from "primereact/calendar";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Chart} from "primereact/chart";
import {InputText} from "primereact/inputtext";
import {InputNumber} from "primereact/inputnumber";
import {Dialog} from "primereact/dialog";
import {setCreateButton, setTitle} from "../actions/main";
import {sendRequest} from "../services/Sender";
import formater from "../helpers/formater";
import Loader from "../components/Loader";

// Продажи по артистам (для отдела продаж): выручка от билетов, цена, заполняемость, интервалы.
// Выручка — приходы кассы по мероприятию; билеты — из карточек мероприятий (заполнены не везде).

const fmt = (d) => {
    const z = (n) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`
}
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x }
const yesterday = () => addDays(new Date(new Date().toDateString()), -1)
const ruDate = (s) => (s ? new Date(s).toLocaleDateString('ru-RU') : '—')

const PRESETS = [
    {label: 'Этот месяц', value: 'this_month'},
    {label: 'Прошлый месяц', value: 'last_month'},
    {label: 'Этот квартал', value: 'this_quarter'},
    {label: 'Прошлый квартал', value: 'last_quarter'},
    {label: 'Этот год', value: 'this_year'},
    {label: 'Прошлый год', value: 'last_year'},
    {label: 'Последние 12 месяцев', value: 'last_12m'},
    {label: 'Свой период', value: 'custom'},
]
const COMPARE = [
    {label: 'Без сравнения', value: 'none'},
    {label: 'Предыдущий период', value: 'prev'},
    {label: 'Тот же период год назад', value: 'yoy'},
    {label: 'Свой период', value: 'custom'},
]
const METRICS = [
    {label: 'выручка от билетов', value: 'revenue', money: true},
    {label: 'выручка за концерт', value: 'revenue_avg', money: true},
    {label: 'средняя цена билета', value: 'avg_price', money: true},
    {label: 'заполняемость зала, %', value: 'fill_pct', money: false},
    {label: 'билетов за концерт', value: 'tickets_avg', money: false},
]

function presetRange(p) {
    const t = new Date(new Date().toDateString())
    const y = yesterday()
    const q = Math.floor(t.getMonth() / 3)
    switch (p) {
        case 'this_month': return [new Date(t.getFullYear(), t.getMonth(), 1), y]
        case 'last_month': return [new Date(t.getFullYear(), t.getMonth() - 1, 1), new Date(t.getFullYear(), t.getMonth(), 0)]
        case 'this_quarter': return [new Date(t.getFullYear(), q * 3, 1), y]
        case 'last_quarter': return [new Date(t.getFullYear(), q * 3 - 3, 1), new Date(t.getFullYear(), q * 3, 0)]
        case 'this_year': return [new Date(t.getFullYear(), 0, 1), y]
        case 'last_year': return [new Date(t.getFullYear() - 1, 0, 1), new Date(t.getFullYear() - 1, 11, 31)]
        case 'last_12m': return [new Date(t.getFullYear() - 1, t.getMonth(), t.getDate()), y]
        default: return null
    }
}
function compareRange(mode, [d1, d2]) {
    if (mode === 'prev') {
        const len = Math.round((d2 - d1) / 86400000) + 1
        return [addDays(d1, -len), addDays(d1, -1)]
    }
    if (mode === 'yoy') {
        const shift = (d) => new Date(d.getFullYear() - 1, d.getMonth(), d.getDate())
        return [shift(d1), shift(d2)]
    }
    return null
}

const money = (v) => (v === null || v === undefined ? '—' : formater.amountFormat(v))
const num = (v, suf = '') => (v === null || v === undefined ? '—' : `${Number(v).toLocaleString('ru-RU')}${suf}`)
const pctTpl = (v) => {
    if (v === null || v === undefined) return <span style={{color: '#aaa'}}>—</span>
    const up = v >= 0
    return <span style={{color: up ? '#1a9b5b' : '#d64545', fontWeight: 600}}>{up ? '▲' : '▼'} {Math.abs(v).toFixed(0)}%</span>
}
const pct = (cur, prev) => (cur === null || cur === undefined || !prev ? null : (cur - prev) / prev * 100)

function ArtistHistory({artist, onHide}) {
    const [data, setData] = useState(null)
    useEffect(() => {
        if (!artist) return
        setData(null)
        sendRequest('analytics/artist-sales/history', 'GET', {key: artist.key}, true).then(setData)
    }, [artist])
    if (!artist) return null
    const shows = data ? data.shows : []
    const past = shows.filter((s) => !s.upcoming && s.revenue > 0)
    const chartData = {
        labels: past.map((s) => ruDate(s.date)),
        datasets: [
            {type: 'bar', label: 'Выручка от билетов', backgroundColor: '#7c2d12', data: past.map((s) => Math.round(s.revenue)), yAxisID: 'y'},
            {type: 'line', label: 'Средняя цена билета', borderColor: '#d4893f', backgroundColor: '#d4893f', tension: 0.3,
                data: past.map((s) => s.avg_price ? Math.round(s.avg_price) : null), yAxisID: 'y1', spanGaps: true},
        ],
    }
    const chartOptions = {
        maintainAspectRatio: false,
        plugins: {legend: {position: 'bottom'}, datalabels: {display: false},
            tooltip: {callbacks: {label: (c) => `${c.dataset.label}: ${formater.amountFormat(c.raw)}`}}},
        scales: {
            y: {position: 'left', ticks: {callback: (v) => (v >= 1000000 ? (v / 1000000).toFixed(1) + ' млн' : Math.round(v / 1000) + ' тыс')}},
            y1: {position: 'right', grid: {drawOnChartArea: false}, ticks: {callback: (v) => Math.round(v / 1000) + ' тыс'}},
        },
    }
    // интервал между концертами и выручка: короткие (<60 дн.) vs длинные
    const withGap = past.filter((s) => s.gap_days !== null)
    const short = withGap.filter((s) => s.gap_days < 60), long = withGap.filter((s) => s.gap_days >= 60)
    const avg = (a) => (a.length ? a.reduce((x, s) => x + s.revenue, 0) / a.length : null)
    const upcoming = shows.filter((s) => s.upcoming)
    return (
        <Dialog header={artist.name} visible={!!artist} onHide={onHide} style={{width: '90vw', maxWidth: 1100}} modal dismissableMask>
            {!data ? <Loader/> : <>
                <div className="row mb-2">
                    <div className="col-md-3 col-6"><div style={{color: '#888'}}>Концертов всего</div><h4>{past.length}</h4></div>
                    <div className="col-md-3 col-6"><div style={{color: '#888'}}>Выручка за концерт (вся история)</div><h4>{money(avg(past))}</h4></div>
                    <div className="col-md-3 col-6"><div style={{color: '#888'}}>При интервале &lt; 60 дней</div><h4>{money(avg(short))}</h4><small style={{color: '#888'}}>{short.length} конц.</small></div>
                    <div className="col-md-3 col-6"><div style={{color: '#888'}}>При интервале ≥ 60 дней</div><h4>{money(avg(long))}</h4><small style={{color: '#888'}}>{long.length} конц.</small></div>
                </div>
                {upcoming.length > 0 && <div className="alert alert-info">
                    Ближайшие: {upcoming.map((s) => `${ruDate(s.date)} (${s.weekday})`).join(', ')}
                </div>}
                <div style={{height: 300}}><Chart type="bar" data={chartData} options={chartOptions} style={{height: '100%'}}/></div>
                <DataTable value={[...shows].reverse()} size="small" stripedRows className="mt-3" paginator rows={15}>
                    <Column field="date" header="Дата" body={(s) => <>{ruDate(s.date)} <span style={{color: '#888'}}>{s.weekday}</span>{s.upcoming && <span style={{color: '#1a7bd6'}}> · впереди</span>}</>}/>
                    <Column field="revenue" header="Выручка" body={(s) => money(s.revenue)}/>
                    <Column field="online_pct" header="Онлайн" body={(s) => num(s.online_pct, '%')}/>
                    <Column field="tickets" header="Билетов" body={(s) => num(s.tickets)}/>
                    <Column field="fill_pct" header="Заполн." body={(s) => num(s.fill_pct, '%')}/>
                    <Column field="avg_price" header="Ср. цена" body={(s) => money(s.avg_price)}/>
                    <Column field="gap_days" header="Интервал, дн." body={(s) => num(s.gap_days)}/>
                    <Column header="" body={(s) => <Link to={'/event/' + s.event_id}>открыть</Link>}/>
                </DataTable>
            </>}
        </Dialog>
    )
}

function ArtistSales() {
    const dispatch = useDispatch()
    const currentDepartment = useSelector((state) => state.currentDepartment)

    const [preset, setPreset] = useState(() => localStorage.getItem('as_preset') || 'this_year')
    const [range, setRange] = useState(() => presetRange(localStorage.getItem('as_preset') || 'this_year'))
    const [cmpMode, setCmpMode] = useState(() => localStorage.getItem('as_cmp') || 'yoy')
    const [cmpRange, setCmpRange] = useState(null)
    const [metric, setMetric] = useState('revenue')
    const [minShows, setMinShows] = useState(1)
    const [search, setSearch] = useState('')
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [artist, setArtist] = useState(null)

    useEffect(() => {
        dispatch(setTitle('Продажи по артистам'))
        dispatch(setCreateButton(false))
    }, [dispatch])

    const effCmp = useMemo(() => {
        if (!range || !range[0] || !range[1] || cmpMode === 'none') return null
        if (cmpMode === 'custom') return cmpRange && cmpRange[0] && cmpRange[1] ? cmpRange : null
        return compareRange(cmpMode, range)
    }, [range, cmpMode, cmpRange])

    useEffect(() => {
        if (!range || !range[0] || !range[1]) return
        localStorage.setItem('as_preset', preset)
        localStorage.setItem('as_cmp', cmpMode)
        const params = {date_from: fmt(range[0]), date_to: fmt(range[1])}
        if (effCmp) { params.cmp_from = fmt(effCmp[0]); params.cmp_to = fmt(effCmp[1]) }
        if (currentDepartment) params.client_id = currentDepartment
        setLoading(true); setError(null)
        sendRequest('analytics/artist-sales', 'GET', params, true)
            .then((res) => { setData(res); setLoading(false) })
            .catch((e) => { setError(String(e.message || e)); setLoading(false) })
    }, [range, effCmp, currentDepartment, preset, cmpMode])

    const onPreset = (p) => { setPreset(p); if (p !== 'custom') setRange(presetRange(p)) }
    const hasCmp = !!(data && data.compare)
    const M = METRICS.find((m) => m.value === metric)

    const rows = useMemo(() => {
        if (!data) return []
        let r = data.items.filter((a) => a.shows_with_revenue >= minShows)
        if (search.trim()) r = r.filter((a) => a.name.toLowerCase().includes(search.trim().toLowerCase()))
        const val = (a) => (a[metric] === null || a[metric] === undefined ? -1 : a[metric])
        return [...r].sort((a, b) => val(b) - val(a)).map((a, i) => ({...a, place: i + 1, cmp_val: a.cmp ? a.cmp[metric] : null}))
    }, [data, minShows, search, metric])

    const chartData = useMemo(() => {
        const top = rows.filter((a) => a[metric] !== null).slice(0, 10)
        const ds = [{label: range ? `${fmt(range[0])} — ${fmt(range[1])}` : 'Период', backgroundColor: '#7c2d12', data: top.map((a) => Math.round(a[metric] || 0))}]
        if (hasCmp) ds.push({label: `${data.compare.from} — ${data.compare.to}`, backgroundColor: '#e0b49a', data: top.map((a) => Math.round(a.cmp_val || 0))})
        return {labels: top.map((a) => a.name.length > 28 ? a.name.slice(0, 27) + '…' : a.name), datasets: ds}
    }, [rows, metric, hasCmp, data, range])

    const chartOptions = {
        indexAxis: 'y', maintainAspectRatio: false,
        plugins: {legend: {position: 'bottom'}, datalabels: {display: false},
            tooltip: {callbacks: {label: (c) => `${c.dataset.label}: ${M.money ? formater.amountFormat(c.raw) : num(c.raw)}`}}},
        scales: {x: {ticks: {callback: (v) => (M.money ? (v >= 1000000 ? (v / 1000000).toFixed(1) + ' млн' : Math.round(v / 1000) + ' тыс') : v)}}},
    }

    const T = data && data.period.totals
    const C = data && data.compare && data.compare.totals
    const card = (title, cur, prev, kind = 'money', hint = null) => {
        const show = (v) => (v === null || v === undefined ? '—' : kind === 'money' ? formater.amountFormat(v) : kind === 'pct' ? `${v}%` : num(v))
        return (
            <div className="col-lg col-md-4 col-6 mb-3">
                <div className="card h-100"><div className="card-body">
                    <div style={{color: '#888', fontSize: 13}}>{title}</div>
                    <h3 style={{margin: '4px 0'}}>{show(cur)}</h3>
                    {C && <div style={{fontSize: 13}}>{pctTpl(pct(cur, prev))} <span style={{color: '#888'}}>было {show(prev)}</span></div>}
                    {hint && <div style={{fontSize: 12, color: '#999'}}>{hint}</div>}
                </div></div>
            </div>
        )
    }

    return (
        <div className="container-fluid">
            <div className="card mb-3"><div className="card-body">
                <div className="row align-items-end">
                    <div className="col-md-3 mb-2"><label>Период</label>
                        <Dropdown value={preset} options={PRESETS} onChange={(e) => onPreset(e.value)} style={{width: '100%'}}/></div>
                    <div className="col-md-3 mb-2"><label>Даты</label>
                        <Calendar value={range} selectionMode="range" dateFormat="dd.mm.yy" readOnlyInput showIcon style={{width: '100%'}}
                                  onChange={(e) => { setPreset('custom'); setRange(e.value) }}/></div>
                    <div className="col-md-3 mb-2"><label>Сравнить с</label>
                        <Dropdown value={cmpMode} options={COMPARE} onChange={(e) => setCmpMode(e.value)} style={{width: '100%'}}/></div>
                    <div className="col-md-3 mb-2">
                        {cmpMode === 'custom' ? (<><label>Даты сравнения</label>
                            <Calendar value={cmpRange} selectionMode="range" dateFormat="dd.mm.yy" readOnlyInput showIcon style={{width: '100%'}}
                                      onChange={(e) => setCmpRange(e.value)}/></>)
                            : (effCmp && <div style={{color: '#888', paddingBottom: 8}}>Сравнение: {fmt(effCmp[0])} — {fmt(effCmp[1])}</div>)}
                    </div>
                </div>
            </div></div>

            {loading && <Loader/>}
            {error && <div className="alert alert-danger">Ошибка загрузки: {error}</div>}

            {!loading && data && <>
                <div className="row">
                    {card('Выручка от билетов', T.revenue, C && C.revenue)}
                    {card('Концертов', T.shows_with_revenue, C && C.shows_with_revenue, 'num')}
                    {card('Выручка за концерт', T.revenue_avg, C && C.revenue_avg)}
                    {card('Средняя цена билета', T.avg_price, C && C.avg_price, 'money', `по ${T.shows_with_tickets} конц. с внесёнными билетами`)}
                    {card('Заполняемость зала', T.fill_pct, C && C.fill_pct, 'pct', `зал ${T.capacity} мест`)}
                </div>

                {T.shows_with_tickets < T.shows_with_revenue && <div className="alert alert-warning">
                    Количество билетов внесено у {T.shows_with_tickets} из {T.shows_with_revenue} концертов — средняя цена и заполняемость
                    посчитаны только по ним. Выручка — по всем концертам. После подключения API Intickets билеты будут заполняться автоматически.
                </div>}

                <div className="card mb-3"><div className="card-body">
                    <div className="row align-items-end mb-2">
                        <div className="col-md-4"><h4 style={{margin: 0}}>Топ-10: {M.label}</h4></div>
                        <div className="col-md-3"><label>Показатель</label>
                            <Dropdown value={metric} options={METRICS} onChange={(e) => setMetric(e.value)} style={{width: '100%'}}/></div>
                        <div className="col-md-2"><label>Мин. концертов</label>
                            <InputNumber value={minShows} onValueChange={(e) => setMinShows(e.value || 1)} min={1} showButtons style={{width: '100%'}}/></div>
                        <div className="col-md-3"><label>Поиск артиста</label>
                            <InputText value={search} onChange={(e) => setSearch(e.target.value)} style={{width: '100%'}}/></div>
                    </div>
                    <div style={{height: 420}}><Chart type="bar" data={chartData} options={chartOptions} style={{height: '100%'}}/></div>
                </div></div>

                <div className="card"><div className="card-body">
                    <div style={{color: '#888', marginBottom: 8}}>Нажмите на артиста — откроется история всех его концертов.</div>
                    <DataTable value={rows} paginator rows={25} size="small" stripedRows responsiveLayout="scroll"
                               emptyMessage="Нет данных за выбранный период">
                        <Column field="place" header="Место" sortable style={{width: 70}}/>
                        <Column field="name" header="Артист" sortable
                                body={(a) => <span role="button" tabIndex={0} style={{color: '#4a5bd4', cursor: 'pointer'}}
                                                  onClick={() => setArtist(a)} onKeyDown={(e) => { if (e.key === 'Enter') setArtist(a) }}>{a.name}</span>}/>
                        <Column field="shows_with_revenue" header="Концертов" sortable/>
                        <Column field="revenue" header="Выручка" sortable body={(a) => money(a.revenue)}/>
                        <Column field="revenue_avg" header="За концерт" sortable body={(a) => money(a.revenue_avg)}/>
                        <Column field="avg_price" header="Ср. цена" sortable body={(a) => money(a.avg_price)}/>
                        <Column field="fill_pct" header="Заполн." sortable body={(a) => num(a.fill_pct, '%')}/>
                        <Column field="online_pct" header="Онлайн" sortable body={(a) => num(a.online_pct, '%')}/>
                        <Column field="avg_gap_days" header="Интервал, дн." sortable body={(a) => num(a.avg_gap_days)}/>
                        <Column field="best_weekday" header="Лучший день"/>
                        <Column field="last_show" header="Последний" sortable body={(a) => ruDate(a.last_show)}/>
                        {hasCmp && <Column header="Выручка (сравн.)" body={(a) => (a.cmp ? money(a.cmp.revenue) : '—')}/>}
                        {hasCmp && <Column field="delta_revenue_pct" header="Δ выручка" sortable body={(a) => pctTpl(a.delta_revenue_pct)}/>}
                        {hasCmp && <Column field="delta_revenue_avg_pct" header="Δ за концерт" sortable body={(a) => pctTpl(a.delta_revenue_avg_pct)}/>}
                        {hasCmp && <Column field="delta_avg_price_pct" header="Δ цена" sortable body={(a) => pctTpl(a.delta_avg_price_pct)}/>}
                        {hasCmp && <Column field="rank_change" header="Место ±" sortable
                                           body={(a) => a.rank_change === null || a.rank_change === undefined ? (a.cmp ? '—' : <span style={{color: '#888'}}>новый</span>)
                                               : a.rank_change === 0 ? '=' : <span style={{color: a.rank_change > 0 ? '#1a9b5b' : '#d64545'}}>{a.rank_change > 0 ? '▲' : '▼'} {Math.abs(a.rank_change)}</span>}/>}
                    </DataTable>
                </div></div>

                {hasCmp && data.only_in_compare.length > 0 && <div className="card mt-3"><div className="card-body">
                    <h5>Выступали только в периоде сравнения ({data.only_in_compare.length})</h5>
                    <div style={{color: '#666'}}>
                        {data.only_in_compare.slice(0, 30).map((a) => `${a.name} — ${money(a.revenue)}`).join(' · ')}
                    </div>
                </div></div>}
            </>}

            <ArtistHistory artist={artist} onHide={() => setArtist(null)}/>
        </div>
    )
}

export default ArtistSales
